import { Injectable } from '@angular/core';
import { NavigationEnd, Params, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * In-memory (per browser tab, per session) store of the last query params of
 * each list screen. Never persisted to localStorage on purpose: filters must
 * not leak between users/tenants sharing the same workstation. Cleared on
 * logout/login through `clearStoredListStates()` (see AccountService).
 */
const storedListStates = new Map<string, Params>();
const trackedListPaths = new Set<string>();

export function clearStoredListStates(): void {
  storedListStates.clear();
}

/** Path part of a router URL (no query string, no fragment). */
export function listPathOf(url: string): string {
  return url.split('#')[0].split('?')[0];
}

/**
 * Keeps list filters when the user comes back to a list screen without query
 * params (sidebar, "Volver", navigation after saving). Lists already keep
 * their filters in the URL; this only remembers the last URL of each list and
 * restores it through `restoreListStateGuard`.
 */
@Injectable({ providedIn: 'root' })
export class ListStateService {
  constructor(private router: Router) {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => this.remember(event.urlAfterRedirects));
  }

  /** Marks `path` as a list whose query params must be remembered. */
  track(path: string): void {
    trackedListPaths.add(path);
  }

  get(path: string): Params | null {
    return storedListStates.get(path) ?? null;
  }

  private remember(url: string): void {
    const path = listPathOf(url);
    if (!trackedListPaths.has(path)) return;

    const params = this.router.parseUrl(url).queryParams;
    if (Object.keys(params).length === 0) {
      // "Limpiar filtros" lands on a clean URL: forget the old filters.
      storedListStates.delete(path);
      return;
    }
    storedListStates.set(path, { ...params });
  }
}
